import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiRequest } from '@/lib/supabase'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

export interface BioProduct {
  id: string
  bio_page_id: string
  section_title: string
  section_layout: 'single' | 'grid' | 'carousel'
  product_name: string
  product_url: string
  image_url: string | null
  secondary_image_url: string | null
  currency: string
  price: number | null
  compare_at_price: number | null
  badge: 'top_pick' | 'sale' | 'new' | 'limited' | 'custom' | null
  badge_text: string | null
  cta_label: string
  cta_style: 'filled' | 'outline' | 'text'
  stock_status: 'in_stock' | 'sold_out'
  order_index: number
  is_active: boolean
  click_count: number
}

interface Props {
  pageId: string
  products: BioProduct[]
}

const BADGE_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'top_pick', label: 'Top Pick' },
  { value: 'sale', label: 'Sale' },
  { value: 'new', label: 'New' },
  { value: 'limited', label: 'Limited' },
  { value: 'custom', label: 'Custom' },
]

const CURRENCY_OPTIONS = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'INR']

export function BioProductEditor({ pageId, products }: Props) {
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const [newProduct, setNewProduct] = useState<{
    product_name: string
    product_url: string
    image_url: string
    price: string
    compare_at_price: string
    currency: string
    badge: string
    badge_text: string
    cta_label: string
    cta_style: 'filled' | 'outline' | 'text'
    stock_status: 'in_stock' | 'sold_out'
    section_title: string
    section_layout: 'single' | 'grid' | 'carousel'
  }>({
    product_name: '',
    product_url: '',
    image_url: '',
    price: '',
    compare_at_price: '',
    currency: 'USD',
    badge: '',
    badge_text: '',
    cta_label: 'Shop Now',
    cta_style: 'filled',
    stock_status: 'in_stock',
    section_title: 'Featured Products',
    section_layout: 'grid',
  })

  const addMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiRequest(`/api/bio-pages/${pageId}/products`, 'POST', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bio-products', pageId] })
      setNewProduct({
        product_name: '', product_url: '', image_url: '', price: '', compare_at_price: '',
        currency: 'USD', badge: '', badge_text: '', cta_label: 'Shop Now', cta_style: 'filled',
        stock_status: 'in_stock', section_title: 'Featured Products', section_layout: 'grid',
      })
      setShowAdd(false)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ productId, data }: { productId: string; data: Partial<BioProduct> }) =>
      apiRequest(`/api/bio-pages/${pageId}/products/${productId}`, 'PUT', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bio-products', pageId] })
      setEditingId(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (productId: string) =>
      apiRequest(`/api/bio-pages/${pageId}/products/${productId}`, 'DELETE'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bio-products', pageId] })
    },
  })

  const reorderMutation = useMutation({
    mutationFn: (productIds: string[]) =>
      apiRequest(`/api/bio-pages/${pageId}/products/reorder`, 'POST', { productIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bio-products', pageId] })
    },
  })

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = products.findIndex(p => p.id === active.id)
    const newIndex = products.findIndex(p => p.id === over.id)
    const newOrder = arrayMove(products, oldIndex, newIndex)
    reorderMutation.mutate(newOrder.map(p => p.id))
  }

  const handleAdd = () => {
    addMutation.mutate({
      product_name: newProduct.product_name,
      product_url: newProduct.product_url,
      image_url: newProduct.image_url || undefined,
      price: newProduct.price ? parseFloat(newProduct.price) : undefined,
      compare_at_price: newProduct.compare_at_price ? parseFloat(newProduct.compare_at_price) : undefined,
      currency: newProduct.currency,
      badge: newProduct.badge || undefined,
      badge_text: newProduct.badge_text || undefined,
      cta_label: newProduct.cta_label,
      cta_style: newProduct.cta_style,
      stock_status: newProduct.stock_status,
      section_title: newProduct.section_title,
      section_layout: newProduct.section_layout,
    })
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Products ({products.length})</h3>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="text-sm text-[#1754cf] hover:underline flex items-center gap-1"
        >
          <span className="material-symbols-outlined text-[16px]">add</span>
          Add Product
        </button>
      </div>

      {/* Add product form */}
      {showAdd && (
        <div className="bg-gray-50 dark:bg-[#0d121b] p-4 rounded-lg border border-gray-200 dark:border-white/10 space-y-3">
          {/* Section settings */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Section Title</label>
              <input
                type="text"
                value={newProduct.section_title}
                onChange={e => setNewProduct(p => ({ ...p, section_title: e.target.value }))}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1c2230] text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Layout</label>
              <select
                value={newProduct.section_layout}
                onChange={e => setNewProduct(p => ({ ...p, section_layout: e.target.value as 'single' | 'grid' | 'carousel' }))}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1c2230] text-gray-900 dark:text-white"
              >
                <option value="grid">Grid</option>
                <option value="single">Single Column</option>
                <option value="carousel">Carousel</option>
              </select>
            </div>
          </div>

          {/* Product details */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Product Name *</label>
              <input
                type="text"
                value={newProduct.product_name}
                onChange={e => setNewProduct(p => ({ ...p, product_name: e.target.value }))}
                placeholder="Product name"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1c2230] text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Product URL *</label>
              <input
                type="text"
                value={newProduct.product_url}
                onChange={e => setNewProduct(p => ({ ...p, product_url: e.target.value }))}
                placeholder="https://amazon.com/..."
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1c2230] text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Image URL</label>
            <input
              type="text"
              value={newProduct.image_url}
              onChange={e => setNewProduct(p => ({ ...p, image_url: e.target.value }))}
              placeholder="https://example.com/image.jpg"
              className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1c2230] text-gray-900 dark:text-white"
            />
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Currency</label>
              <select
                value={newProduct.currency}
                onChange={e => setNewProduct(p => ({ ...p, currency: e.target.value }))}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1c2230] text-gray-900 dark:text-white"
              >
                {CURRENCY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Price</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={newProduct.price}
                onChange={e => setNewProduct(p => ({ ...p, price: e.target.value }))}
                placeholder="29.99"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1c2230] text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Compare At</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={newProduct.compare_at_price}
                onChange={e => setNewProduct(p => ({ ...p, compare_at_price: e.target.value }))}
                placeholder="49.99"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1c2230] text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* Badge & CTA */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Badge</label>
              <select
                value={newProduct.badge}
                onChange={e => setNewProduct(p => ({ ...p, badge: e.target.value }))}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1c2230] text-gray-900 dark:text-white"
              >
                {BADGE_OPTIONS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
            </div>
            {newProduct.badge === 'custom' && (
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Badge Text</label>
                <input
                  type="text"
                  value={newProduct.badge_text}
                  onChange={e => setNewProduct(p => ({ ...p, badge_text: e.target.value }))}
                  placeholder="Custom badge"
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1c2230] text-gray-900 dark:text-white"
                />
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">CTA Label</label>
              <input
                type="text"
                value={newProduct.cta_label}
                onChange={e => setNewProduct(p => ({ ...p, cta_label: e.target.value }))}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1c2230] text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">CTA Style</label>
              <select
                value={newProduct.cta_style}
                onChange={e => setNewProduct(p => ({ ...p, cta_style: e.target.value as 'filled' | 'outline' | 'text' }))}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1c2230] text-gray-900 dark:text-white"
              >
                <option value="filled">Filled</option>
                <option value="outline">Outline</option>
                <option value="text">Text Only</option>
              </select>
            </div>
          </div>

          {/* Stock status */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Stock Status</label>
            <select
              value={newProduct.stock_status}
              onChange={e => setNewProduct(p => ({ ...p, stock_status: e.target.value as 'in_stock' | 'sold_out' }))}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1c2230] text-gray-900 dark:text-white"
            >
              <option value="in_stock">In Stock</option>
              <option value="sold_out">Sold Out</option>
            </select>
          </div>

          {addMutation.isError && (
            <p className="text-xs text-red-500">{(addMutation.error as Error)?.message || 'Failed to add product'}</p>
          )}
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg">
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={!newProduct.product_name || !newProduct.product_url || addMutation.isPending}
              className="px-3 py-1.5 text-xs font-medium text-white bg-[#1754cf] rounded-lg hover:opacity-90 disabled:opacity-50"
            >
              {addMutation.isPending ? 'Adding...' : 'Add Product'}
            </button>
          </div>
        </div>
      )}

      {/* Products list */}
      {products.length === 0 && !showAdd ? (
        <div className="text-center py-8">
          <span className="material-symbols-outlined text-4xl text-gray-300 dark:text-gray-600 mb-2 block">storefront</span>
          <p className="text-sm text-gray-400 mb-1">No products yet</p>
          <p className="text-xs text-gray-400">Add affiliate products and featured items to create a shoppable storefront.</p>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={products.map(p => p.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {products.map(product => (
                <SortableProductItem
                  key={product.id}
                  product={product}
                  editingId={editingId}
                  setEditingId={setEditingId}
                  updateMutation={updateMutation}
                  deleteMutation={deleteMutation}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  )
}

function SortableProductItem({ product, editingId, setEditingId, updateMutation, deleteMutation }: {
  product: BioProduct
  editingId: string | null
  setEditingId: (id: string | null) => void
  updateMutation: ReturnType<typeof useMutation<unknown, Error, { productId: string; data: Partial<BioProduct> }>>
  deleteMutation: ReturnType<typeof useMutation<unknown, Error, string>>
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: product.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white dark:bg-[#1c2230] border border-gray-200 dark:border-white/10 rounded-lg p-3 flex items-center gap-3"
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing touch-none"
      >
        <span className="material-symbols-outlined text-[18px]">drag_indicator</span>
      </button>

      {/* Product image */}
      {product.image_url ? (
        <img
          src={product.image_url}
          alt={product.product_name}
          className="w-12 h-12 rounded-lg object-cover shrink-0"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
        />
      ) : (
        <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-white/5 flex items-center justify-center shrink-0">
          <span className="material-symbols-outlined text-gray-400 text-[20px]">image</span>
        </div>
      )}

      {/* Product info */}
      <div className="flex-1 min-w-0">
        {editingId === product.id ? (
          <EditProductForm
            product={product}
            onSave={(data) => updateMutation.mutate({ productId: product.id, data })}
            onCancel={() => setEditingId(null)}
            saving={updateMutation.isPending}
          />
        ) : (
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{product.product_name}</span>
              {product.badge && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                  {product.badge === 'custom' ? product.badge_text : product.badge.replace('_', ' ')}
                </span>
              )}
              {product.stock_status === 'sold_out' && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">Sold Out</span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              {product.price != null && (
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                  {product.currency} {Number(product.price).toFixed(2)}
                </span>
              )}
              {product.compare_at_price != null && (
                <span className="text-xs text-gray-400 line-through">
                  {product.currency} {Number(product.compare_at_price).toFixed(2)}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 truncate mt-0.5">{product.product_url}</p>
            <span className="text-[10px] text-gray-400">{product.click_count} clicks</span>
          </div>
        )}
      </div>

      {/* Actions */}
      {editingId !== product.id && (
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => updateMutation.mutate({ productId: product.id, data: { is_active: !product.is_active } })}
            className={`p-1 rounded ${product.is_active ? 'text-green-500' : 'text-gray-300'}`}
            title={product.is_active ? 'Active' : 'Inactive'}
          >
            <span className="material-symbols-outlined text-[18px]">
              {product.is_active ? 'toggle_on' : 'toggle_off'}
            </span>
          </button>
          <button
            onClick={() => setEditingId(product.id)}
            className="p-1 text-gray-400 hover:text-[#1754cf]"
          >
            <span className="material-symbols-outlined text-[18px]">edit</span>
          </button>
          <button
            onClick={() => { if (confirm('Delete this product?')) deleteMutation.mutate(product.id) }}
            className="p-1 text-gray-400 hover:text-red-500"
          >
            <span className="material-symbols-outlined text-[18px]">delete</span>
          </button>
        </div>
      )}
    </div>
  )
}

function EditProductForm({ product, onSave, onCancel, saving }: {
  product: BioProduct
  onSave: (data: Partial<BioProduct>) => void
  onCancel: () => void
  saving: boolean
}) {
  const [form, setForm] = useState({
    product_name: product.product_name,
    product_url: product.product_url,
    image_url: product.image_url || '',
    price: product.price != null ? String(product.price) : '',
    compare_at_price: product.compare_at_price != null ? String(product.compare_at_price) : '',
    currency: product.currency,
    badge: product.badge || '',
    badge_text: product.badge_text || '',
    cta_label: product.cta_label,
    cta_style: product.cta_style,
    stock_status: product.stock_status,
  })

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <input
          type="text"
          value={form.product_name}
          onChange={e => setForm(p => ({ ...p, product_name: e.target.value }))}
          placeholder="Product name"
          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white"
        />
        <input
          type="text"
          value={form.product_url}
          onChange={e => setForm(p => ({ ...p, product_url: e.target.value }))}
          placeholder="Product URL"
          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white"
        />
      </div>
      <input
        type="text"
        value={form.image_url}
        onChange={e => setForm(p => ({ ...p, image_url: e.target.value }))}
        placeholder="Image URL"
        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white"
      />
      <div className="grid grid-cols-3 gap-2">
        <select
          value={form.currency}
          onChange={e => setForm(p => ({ ...p, currency: e.target.value }))}
          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white"
        >
          {CURRENCY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input
          type="number"
          step="0.01"
          value={form.price}
          onChange={e => setForm(p => ({ ...p, price: e.target.value }))}
          placeholder="Price"
          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white"
        />
        <input
          type="number"
          step="0.01"
          value={form.compare_at_price}
          onChange={e => setForm(p => ({ ...p, compare_at_price: e.target.value }))}
          placeholder="Compare at"
          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white"
        />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <select
          value={form.badge}
          onChange={e => setForm(p => ({ ...p, badge: e.target.value }))}
          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white"
        >
          {BADGE_OPTIONS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
        </select>
        <input
          type="text"
          value={form.cta_label}
          onChange={e => setForm(p => ({ ...p, cta_label: e.target.value }))}
          placeholder="CTA label"
          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white"
        />
        <select
          value={form.stock_status}
          onChange={e => setForm(p => ({ ...p, stock_status: e.target.value as 'in_stock' | 'sold_out' }))}
          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white"
        >
          <option value="in_stock">In Stock</option>
          <option value="sold_out">Sold Out</option>
        </select>
      </div>
      <div className="flex gap-2">
        <button onClick={onCancel} className="px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5 rounded">Cancel</button>
        <button
          onClick={() => onSave({
            product_name: form.product_name,
            product_url: form.product_url,
            image_url: form.image_url || null,
            price: form.price ? parseFloat(form.price) : null,
            compare_at_price: form.compare_at_price ? parseFloat(form.compare_at_price) : null,
            currency: form.currency,
            badge: (form.badge || null) as BioProduct['badge'],
            badge_text: form.badge_text || null,
            cta_label: form.cta_label,
            cta_style: form.cta_style,
            stock_status: form.stock_status,
          })}
          disabled={saving}
          className="px-2 py-1 text-xs font-medium text-white bg-[#1754cf] rounded hover:opacity-90 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  )
}
